package com.nonopn.skydome.common.app.pages.estimates

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.padding
import androidx.compose.material.FloatingActionButton
import androidx.compose.material.Icon
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.Add
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.TextButton
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.nonopn.skydome.common.app.api.Mode
import com.nonopn.skydome.common.app.api.impl.ThunderDetectionEstimate
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.navigation.RouteEstimateExisting
import com.nonopn.skydome.common.app.navigation.RouteEstimateExistingAdmin
import com.nonopn.skydome.common.app.navigation.RouterEstimateExisting
import com.nonopn.skydome.common.app.navigation.RouterEstimateExistingAdmin
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.estimate_toggle_cancel
import com.nonopn.skywatcher.common.app.res.estimate_toggle_confirm
import com.nonopn.skywatcher.common.app.res.estimate_toggle_disable_title
import com.nonopn.skywatcher.common.app.res.estimate_toggle_enable_title
import com.nonopn.skywatcher.common.app.res.estimate_toggle_error
import com.nonopn.skywatcher.common.app.res.estimate_toggle_text
import com.nonopn.skywatcher.common.app.res.estimates_list_empty
import com.nonopn.skywatcher.common.app.res.estimates_no_match
import com.nonopn.skywatcher.common.app.res.menu_user_estimate_new
import com.nonopn.skywatcher.design.utils.localized
import com.nonopn.skywatcher.design.widgets.abslists.AbstractLists
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.navigation.LocalNavigator
import eu.codlab.viewmodel.rememberViewModel
import org.jetbrains.compose.resources.stringResource

@Composable
fun EstimatesScreen(
    modifier: Modifier,
    mode: Mode,
    onCreateNewEstimate: (() -> Unit)?
) {
    val appModel = LocalApp.current
    val model = rememberViewModel { EstimatesModel(appModel, mode) }
    val modelState by model.states.collectAsState()
    val navigator = LocalNavigator.current

    // Estimate waiting for the confirmation of an activate / deactivate request (admin mode only)
    var pendingToggle by remember { mutableStateOf<Pair<ThunderDetectionEstimate, Boolean>?>(null) }

    Box(
        modifier = modifier,
    ) {
        AbstractLists(
            model = model,
            header = {
                EstimatesFilterBar(
                    query = modelState.searchQuery,
                    statusFilter = modelState.statusFilter,
                    shown = modelState.modelState.filtered.size,
                    total = modelState.modelState.objects.size,
                    onChange = { query, status -> model.applyFilter(query, status) }
                )
            },
            renderElement = { _, holder ->
                RenderEstimate(
                    estimate = holder,
                    showProbes = mode == Mode.Admin,
                    onToggleEnabled = if (mode == Mode.Admin) {
                        { estimate, enabled -> pendingToggle = estimate to enabled }
                    } else {
                        null
                    }
                ) {
                    // The admin list opens the admin editor (enabled checkbox, end date, save)
                    val destination = when (mode) {
                        Mode.Admin -> RouterEstimateExistingAdmin.navigateTo(
                            instance = RouteEstimateExistingAdmin(it.uuid)
                        )

                        Mode.Normal -> RouterEstimateExisting.navigateTo(
                            instance = RouteEstimateExisting(it.uuid)
                        )
                    }
                    navigator.navigateTo(navigateTo = destination)
                }
            }
        )

        pendingToggle?.let { (estimate, enabled) ->
            AlertDialog(
                onDismissRequest = { pendingToggle = null },
                title = {
                    TextNormal(
                        text = stringResource(
                            if (enabled) {
                                Res.string.estimate_toggle_enable_title
                            } else {
                                Res.string.estimate_toggle_disable_title
                            }
                        )
                    )
                },
                text = { TextNormal(text = stringResource(Res.string.estimate_toggle_text, estimate.email)) },
                confirmButton = {
                    TextButton(
                        onClick = {
                            pendingToggle = null
                            model.setEnabled(estimate, enabled)
                        }
                    ) {
                        TextNormal(text = stringResource(Res.string.estimate_toggle_confirm))
                    }
                },
                dismissButton = {
                    TextButton(onClick = { pendingToggle = null }) {
                        TextNormal(text = stringResource(Res.string.estimate_toggle_cancel))
                    }
                }
            )
        }

        if (modelState.toggleError) {
            AlertDialog(
                onDismissRequest = { model.dismissToggleError() },
                text = { TextNormal(text = stringResource(Res.string.estimate_toggle_error)) },
                confirmButton = {
                    TextButton(onClick = { model.dismissToggleError() }) {
                        TextNormal(text = stringResource(Res.string.estimate_toggle_confirm))
                    }
                }
            )
        }

        if (modelState.modelState.filtered.isEmpty()) {
            TextNormal(
                modifier = Modifier.align(Alignment.Center),
                text = if (modelState.modelState.objects.isEmpty()) {
                    Res.string.estimates_list_empty.localized()
                } else {
                    // Estimates exist but none matches the search / status chip
                    Res.string.estimates_no_match.localized()
                },
            )
        }

        onCreateNewEstimate?.let { onCreate ->
            Column(
                modifier = Modifier.align(Alignment.BottomEnd).padding(12.dp),
            ) {
                FloatingActionButton(
                    onClick = onCreate,
                ) {
                    Icon(
                        modifier = Modifier,
                        imageVector = Icons.Rounded.Add,
                        contentDescription = Res.string.menu_user_estimate_new.localized(),
                    )
                }
            }
        }
    }
}
